/**
 * Domain contracts shared by the SPA, the mock API and (via OpenAPI codegen in
 * pass two) the .NET API. Field names are the API's wire names (camelCase).
 *
 * Conventions
 * - Money is a decimal string ("12345.67"); never a JS number on the wire.
 * - Calendar dates are IsoDate ("2026-08-18", AD). Instants are ISO-8601 UTC.
 * - Every tenant-scoped entity carries no tenantId on the wire: the tenant is the
 *   authenticated organization (RLS enforces it server-side).
 * - Document numbers are allocated by the server at issue time (gapless per
 *   sequence per fiscal year). Drafts have `number: null`.
 * - Lists use keyset pagination: `{ items, next }` where `next` is an opaque cursor.
 */
import type { IsoDate } from "@/lib/dates";
import type { Money } from "@/lib/money";
import type { Permission, RoleName } from "@/lib/permissions";

export type { IsoDate, Money, Permission, RoleName };
export type Id = string;

// ───────────────────────── Session & tenancy ─────────────────────────

export interface Organization {
  id: Id;
  name: string;
  legalName: string;
  vatRegistered: boolean;
  country: string;
  currency: string;
  /** The demo company, full of sample books: its mail is simulated and it isn't billed (ADR-0063). */
  sampleData?: boolean;
}

export interface SessionUser {
  id: Id;
  fullName: string;
  email: string;
  initials: string;
  role: RoleName;
  phone?: string;
}

export type Calendar = "gregorian" | "bikram-sambat";

export interface Session {
  user: SessionUser;
  organization: Organization;
  /** Where the person can switch to. `demo` marks their demo company. */
  organizations: Array<Pick<Organization, "id" | "name"> & { demo?: boolean }>;
  permissions: Permission[];
  /** The year today falls in; null when the organisation has none covering today. */
  fiscalYear: FiscalYear | null;
  /** The calendar of the organisation's localization pack (ADR-0033). */
  calendar: Calendar;
  /** What the organisation calls its tax registration and its taxes (ADR-0036). */
  taxNames: TaxNames;
  /** Whether this deployment can make PDFs of documents. */
  documentPdf?: boolean;
  /** Whether this deployment can email documents. */
  documentMail?: boolean;
  /** Whether this deployment can invite new people by email. */
  invitations?: boolean;
}

export type BrandingTemplate = "classic" | "centred" | "compact";

export type BrandingColour = "jade" | "ink" | "slate" | "maroon";

/** How a company's printed documents look. */
export interface DocumentBranding {
  invoiceTemplate: BrandingTemplate;
  receiptTemplate: BrandingTemplate;
  colour: BrandingColour;
  /** Null takes `colour`. */
  receiptColour: BrandingColour | null;
  showLogo: boolean;
  showTaxNumber: boolean;
  showContact: boolean;
  showPaymentDetails: boolean;
  showAmountInWords: boolean;
  showSignature: boolean;
  showDiscount: boolean;
  logoData: string | null;
}

export type DocumentPdfKind = "invoice" | "creditNote" | "receipt" | "quotation" | "purchaseOrder";

/** What an email is about: an invoice, a reminder about one, or a customer's statement. */
export interface EmailTarget {
  kind: "invoice" | "reminder" | "statement" | "quotation" | "purchaseOrder";
  id: Id;
}

/** Where a list of sent emails is read from. */
export interface EmailsOf {
  kind: "invoice" | "customer" | "quotation" | "purchaseOrder";
  id: Id;
}

/** What the Send email dialog starts from. A placeholder with no value is left in, to be replaced. */
export interface EmailDraft {
  title: string;
  to: string;
  subject: string;
  body: string;
  attachment: string;
}

export interface EmailToSend {
  to: string;
  cc: string;
  subject: string;
  body: string;
}

export type EmailState = "preparing" | "sending" | "sent" | "failed" | "skipped" | "bounced";

export interface DocumentEmailRecipient {
  address: string;
  state: EmailState;
  reason?: string;
}

export interface DocumentEmail {
  id: Id;
  subject: string;
  sentAt: string;
  recipients: DocumentEmailRecipient[];
}

export interface DocumentPdfStatus {
  state: "pending" | "ready" | "failed";
  /** A link that lasts a few minutes, once it's ready. */
  url?: string;
}

/** The wording of one of the mails a company sends. */
export interface EmailTemplate {
  kind: string;
  subject: string;
  body: string;
  /** Whether this is the company's own wording, not the default. */
  customised: boolean;
  placeholders: string[];
}

/** What the country pack set for this company. */
export interface CountryPack {
  country: string;
  currencyCode: string;
  grouping: "western" | "lakhCrore";
  calendar: "gregorian" | "bikramSambat";
  fiscalYearStartsMonth: number;
  fiscalYearStartsDay: number;
  roundingScale: number;
  roundingAwayFromZero: boolean;
}

export interface TaxNames {
  registration: string;
  valueAdded: string;
  withholding: string;
  /** The applied pack, e.g. Nepal. Null when none is applied. */
  packName: string | null;
}

// ───────────────────────── Periods ─────────────────────────

export type YearEndMethod = "calculated" | "manual";

/** One thing the books say someone should look at before closing (ADR-0057). */
export interface YearCloseItem {
  key: string;
  kind: string;
  documentId: Id;
  number: string | null;
  date: IsoDate | null;
  party: string;
  amount: Money;
  why: string;
  reviewed: boolean;
}

export interface YearCloseWorkstream {
  id: string;
  name: string;
  evidenceHint: string;
  status: "open" | "done" | "na";
  note: string | null;
  evidence: string | null;
  actedBy: string | null;
  actedAt: string | null;
  items: YearCloseItem[];
  moreItems: number;
}

export interface YearChecklist {
  fiscalYearCode: string;
  endsOn: IsoDate;
  locked: boolean;
  workstreams: YearCloseWorkstream[];
  log: Array<{
    id: Id;
    loggedAt: string;
    loggedBy: string;
    action: string;
    detail: string;
    refused: boolean;
  }>;
}

/** What closing a year would do, and how it closed if it has (ADR-0056). */
export interface YearEndPreview {
  fiscalYearId: Id;
  fiscalYearCode: string;
  nextFiscalYearId: Id | null;
  opensOn: IsoDate | null;
  periodsStillOpen: Array<{
    periodId: Id;
    ordinal: number;
    startsOn: IsoDate;
    endsOn: IsoDate;
    status: number;
  }>;
  carriedForward: Array<{ accountId: Id; code: string; name: string; amount: Money }>;
  /** Income less expense, which goes to retained earnings. */
  result: Money;
  canCarryForward: boolean;
  method: YearEndMethod;
  /** The latest version of how it closed. A reopened year still has one. */
  closure: YearClosure | null;
  /** Every version, oldest first (ADR-0058). */
  closures?: YearClosure[];
  /** Open while an administrator has the year reopened. */
  reopening?: YearReopening | null;
  /** What moved since the year last closed, while it's reopened. */
  difference?: YearCloseChange[] | null;
  /** All the year's months. */
  periods?: YearEndPreview["periodsStillOpen"];
  /** How many journals the year holds. Any posting moves it. */
  revision?: number;
}

/** What has to be true before a year can go for approval or close (ADR-0059). */
export interface YearCloseGates {
  fiscalYearId: Id;
  /** What an approval names: the year, or its reopening when closing again. */
  documentId: Id;
  softLocked: boolean;
  closed: boolean;
  gates: YearCloseGate[];
  /** What 3900 Opening balance equity holds in the year. Opening balances match when it is zero (ADR-0067). */
  openingBalanceEquity: Money;
}

/** One completion gate. The key says which, so a failing one can link to where it is fixed. */
export interface YearCloseGate {
  key: YearCloseGateKey;
  name: string;
  passed: boolean;
  detail: string;
}

export type YearCloseGateKey =
  | "accounts_checked"
  | "adjustments_resolved"
  | "next_year_open"
  | "soft_lock"
  | "opening_balances";

/** What a year's close rests on: its adjusting journals and the trial balance around them. */
export interface YearWorkpaper {
  fiscalYearCode: string;
  endsOn: IsoDate;
  revision: number;
  postedProfit: Money;
  draftsEffect: Money;
  profitIfDraftsPost: Money;
  adjustments: Array<{
    id: Id;
    number: string | null;
    date: IsoDate;
    narration: string;
    status: "draft" | "posted" | "discarded";
    reversesNextYear: boolean;
    profitEffect: Money;
  }>;
  trialBalance: Array<{
    accountId: Id;
    code: string;
    name: string;
    unadjusted: Money;
    adjustments: Money;
    adjusted: Money;
  }>;
}

export interface YearClosure {
  method: YearEndMethod;
  /** None when a reopened year closed again with nothing changed. */
  journalId: Id | null;
  journalNumber: string | null;
  closedAt: string;
  version?: number;
  changes?: YearCloseChange[];
}

export interface YearReopening {
  id: Id;
  reason: string;
  reopenedAt: string;
  periods: YearEndPreview["periodsStillOpen"];
  accounts: Array<{ accountId: Id; code: string; name: string }>;
}

/** One account's closing position, before and after. Signed: positive is a debit. */
export interface YearCloseChange {
  accountId: Id;
  code: string;
  name: string;
  before: Money;
  after: Money;
}

export interface FiscalYear {
  id: Id;
  label: string; // "FY 2025–26"
  start: IsoDate;
  end: IsoDate;
  status: "open" | "locked" | "closed";
  isCurrent: boolean;
}

export interface DayClose {
  closedThrough: IsoDate | null;
  lastClosedBy?: string;
  lastClosedAt?: string;
}

export type YearEndStep = "review" | "preview" | "balance" | "post" | "done";

export interface YearEndClose {
  fiscalYearId: Id;
  step: YearEndStep;
  unposted: Array<{
    id: Id;
    number: string;
    kind: DocumentKind;
    detail: string;
    date: IsoDate;
    amount: Money;
  }>;
  closingJournal: { number: string; date: IsoDate; lines: JournalLineView[] } | null;
  balanceCheck: { closing: Money; opening: Money; difference: Money } | null;
  postedBy?: string;
  postedAt?: string;
}

// ───────────────────────── Parties & accounts ─────────────────────────

export interface Party {
  id: Id;
  kind: "customer" | "supplier" | "employee";
  name: string;
  pan?: string;
  phone?: string;
  email?: string;
  address?: string;
  /** What the demo says the party owes. The API does not carry it; ageing is the ledger's answer. */
  balance?: Money;
  /** The mock's copy of what the API keeps on the party. Null or absent is no limit. */
  creditLimit?: Money;
  /** The mock's copy of the same. Days to pay, as text; absent is no terms. */
  paymentTermsDays?: string;
  /** The mock's copy of the same. Absent is no withholding. */
  withholdingCode?: string;
  /** The mock's copy of the same. */
  legalRegistration?: string;
}

/** Where a party stands today. */
export interface PartyStanding {
  asAt: IsoDate;
  fiscalYearStart: IsoDate | null;
  /** Upper bounds of each band but the last, as on `AgeingReport`. */
  bucketDays: number[];
  buckets: Money[];
  outstanding: Money;
  overdue: Money;
  oldestOverdueDays: number | null;
  /** From the first day of the fiscal year, tax withheld included. */
  settledThisYear: Money;
}

/** A supplier's statement compared with the books. Both balances are as they stood when it was marked. */
export interface SupplierReconciliation {
  id: Id;
  statementDate: IsoDate;
  statementBalance: Money;
  bookBalance: Money;
  /** The statement's balance less ours. */
  difference: Money;
  recordedAt: string;
}

/** One party's own lines, with an opening balance that is theirs. */
/** One employee's place in the books (ADR-0060). */
export interface EmployeeLedgerRow {
  id: Id;
  name: string;
  taxId: string | null;
  /** Positive is owed to the employee. Negative is recoverable from them. */
  balance: Money;
  lastMovement: IsoDate | null;
  isActive: boolean;
}

/** One payment to or receipt from an employee. With no number, it's waiting to be posted. */
export interface EmployeeMovement {
  id: Id;
  partyId: Id;
  partyName: string;
  kind: "payment" | "receipt";
  controlRole: "employee_payable" | "employee_advance";
  amount: Money;
  date: IsoDate;
  cashAccountName: string;
  reference: string | null;
  note: string | null;
  number: string | null;
}

export interface NewEmployeeMovement {
  partyId: Id;
  kind: "payment" | "receipt";
  controlRole: "employee_payable" | "employee_advance";
  amount: string;
  date: IsoDate;
  cashAccountId: Id;
  reference?: string;
  note?: string;
}

export interface PartyStatement {
  from: IsoDate;
  to: IsoDate;
  opening: Money;
  closing: Money;
  totalDebit: Money;
  totalCredit: Money;
  allLines: number;
  lines: Array<{
    journalId: Id;
    documentId: Id;
    date: IsoDate;
    documentType: string;
    number: string;
    details: string | null;
    debit: Money;
    credit: Money;
    balance: Money;
  }>;
}

/** A customer or supplier as a person edits it. An empty field is absent on the wire. */
export interface PartyForm {
  kind: Party["kind"];
  legalName: string;
  tradingName: string;
  pan: string;
  vatRegistered: boolean;
  /** The entry in the company register. Empty is none. */
  legalRegistration: string;
  street: string;
  city: string;
  phone: string;
  email: string;
  /** Empty is no limit. */
  creditLimit: string;
  /** Days to pay, as text. Empty is no terms, and "0" is due on receipt. */
  paymentTermsDays: string;
  /** The category usually withheld from a supplier. Empty is none. */
  withholdingCode: string;
}

export type AccountGroup = "Assets" | "Liabilities" | "Equity" | "Income" | "Expenses";

export interface Account {
  id: Id;
  code: string; // "1200"
  name: string;
  group: AccountGroup;
  isActive: boolean;
  /** A role names it, so a posting rule writes to it. A sales line never should. */
  hasRole?: boolean;
  /** Its balance on its normal side (the chart overview), or what the mock's chart carries. */
  balance?: Money;
  /** The account type within its class (ADR-0066): "current_asset", "operating_expense" and the rest. */
  subtype?: AccountSubtype | null;
  /** Where it sits on a statement, a statement-line code. Null reads "not in layout". */
  statementLine?: string | null;
  normalBalance?: "debit" | "credit" | null;
  /** One of the accounts every company has. Locked: no edit, no remove, no deactivate. */
  isRequired?: boolean;
  /** Why it is required, for the lock's tooltip. */
  requiredReason?: string | null;
  postingRule?: PostingRule;
  /** "required", "bank", "tenant", or "template:<code>@<edition>". */
  source?: string;
}

export type AccountSubtype =
  | "current_asset"
  | "non_current_asset"
  | "current_liability"
  | "non_current_liability"
  | "contributed_equity"
  | "retained_earnings"
  | "reserves"
  | "other_equity"
  | "revenue"
  | "other_income"
  | "finance_income"
  | "cost_of_sales"
  | "operating_expense"
  | "finance_cost"
  | "tax_expense"
  | "other_expense";

/** Who may post to an account (ADR-0066). documents_only and calculated never take a manual journal. */
export type PostingRule =
  | "any"
  | "documents_only"
  | "documents_and_vat_return"
  | "documents_and_journals"
  | "year_end_and_journals"
  | "opening_and_journals"
  | "calculated";

/** The chart as Settings shows it, and whether anything has posted yet. */
export interface ChartOverview {
  accounts: Account[];
  ledgerHasPostings: boolean;
}

/** A supplied chart on offer. */
export interface CoaTemplate {
  code: string;
  version: number;
  name: string;
  description: string;
  isDefault: boolean;
  accountCount: number;
  /** "NP" when it is recommended for Nepal; null for anywhere. */
  region: string | null;
  edition: string | null;
  basis: string | null;
}

export interface CoaTemplateAccount {
  code: string;
  name: string;
  group: AccountGroup;
  subtype: AccountSubtype | null;
  statementLine: string | null;
  normalBalance: "debit" | "credit" | null;
}

export interface CoaTemplateDetail {
  template: CoaTemplate;
  accounts: CoaTemplateAccount[];
}

/** Something that holds on to an account. */
export interface AccountReference {
  accountId: Id;
  code: string;
  name: string;
  /** posted, journal, invoice, quotation, receipt, billing_item, bill, purchase_order, payment, employee_movement,
   * bank_account, fund_transfer, report_mapping, year_end. */
  kind: string;
  uses: number;
}

export interface AccountUsage {
  accountId: Id;
  /** Debits less credits posted to it. */
  posted: Money;
  references: AccountReference[];
}

/** What applying a template now would replace, and what would stop it. */
export interface ReplacePreview {
  ledgerHasPostings: boolean;
  /** Every account but the required and bank and cash ones. */
  replaceable: number;
  createdByCompany: number;
  blocking: AccountReference[];
}

export interface TemplateApplied {
  code: string;
  version: number;
  removed: string[];
  added: string[];
}

export interface NewAccountForm {
  code: string;
  name: string;
  group: AccountGroup;
  subtype: AccountSubtype | null;
  statementLine: string | null;
}

export interface AccountEdit {
  name: string;
  subtype: AccountSubtype | null;
  statementLine: string | null;
}

export interface BankAccount {
  id: Id;
  accountId: Id; // ledger account
  name: string; // "Nabil Bank — current"
  number?: string; // "01201010012345"
  kind: "bank" | "cash";
  currency: string;
  balance: Money;
}

/** A line the bank has that we haven't accounted for. Positive is money in. */
export interface StatementLineToReview {
  id: Id;
  date: IsoDate;
  description: string;
  amount: Money;
  reference: string | null;
}

/** How a company's invoices are set up. */
export interface InvoiceSetting {
  /** The HS code field on lines, and its column on printed invoices. */
  showHsCode: boolean;
  /** The breakdown on the invoice editor. The document carries it either way. */
  showVatBreakdown: boolean;
  footerText: string;
  /** A discount and a charge on each invoice line. */
  lineDiscounts: boolean;
  /** A discount on the whole invoice. A charge on the whole invoice is always offered. */
  documentDiscounts: boolean;
  /** Whether a new invoice starts as a cash sale. */
  cashSaleByDefault: boolean;
}

/** A charge a company adds to invoices, such as freight. An invoice keeps its own copy. */
export interface AdditionalCharge {
  id: Id;
  name: string;
  reasonCode: string;
  category: VatCategory;
  rate: string | null;
  /** What a new row starts with. Empty when it varies. */
  defaultAmount: string;
}

/** A discount or a charge on a whole invoice. */
export interface DocumentAdjustment {
  isCharge: boolean;
  amount: string;
  category: VatCategory;
  rate: string | null;
  reason: string;
}

/** An account agreed with its statement up to a date, and locked there until reopened. */
export interface ReconciliationLock {
  id: Id;
  asAt: IsoDate;
  statementBalance: Money;
  ledgerBalance: Money;
  /** What the ledger says on that date now. A difference is a posting since it was locked. */
  ledgerBalanceNow: Money;
  lineCount: number;
  lockedAt: string;
  reopenedAt: string | null;
}

/** The journal voucher made for a statement line, and why it wasn't matched, if it wasn't. */
export interface StatementEntry {
  number: string;
  matchProblem: string | null;
}

/** A movement we've posted that the bank hasn't shown. Positive is money in. */
export interface LedgerMovement {
  journalId: Id;
  journalLineId: Id;
  documentType: string;
  number: string;
  date: IsoDate;
  amount: Money;
  memo: string | null;
}

export interface MatchedStatementLine {
  matchId: Id;
  lineId: Id;
  date: IsoDate;
  description: string;
  amount: Money;
  reference: string | null;
  documentType: string;
  number: string;
  postingDate: IsoDate;
}

export interface BankReconciliation {
  asAt: IsoDate;
  ledgerBalance: Money;
  /** Null when what's been imported states no balance. */
  statementBalance: Money | null;
  unmatchedOnStatement: Money;
  unmatchedInLedger: Money;
  /** Null without a statement balance, because there's nothing to reconcile to. */
  difference: Money | null;
  onStatementOnly: StatementLineToReview[];
  inLedgerOnly: LedgerMovement[];
}

/** How to read a CSV. Columns count from 0, and an amount is one column or a debit and credit pair. */
export interface StatementFormat {
  dateFormat: string;
  dateColumn: number;
  descriptionColumn: number;
  delimiter: string;
  hasHeader: boolean;
  amountColumn?: number;
  debitColumn?: number;
  creditColumn?: number;
  referenceColumn?: number;
  balanceColumn?: number;
}

export interface StatementImportResult {
  linesImported: number;
  problems: Array<{ lineNumber: number; column: string; message: string }>;
  /** How many look the same as a line already on the account that day. They are still imported. */
  suspectedRepeats: number;
}

export type JournalStatus = "draft" | "posted" | "discarded";

export interface JournalSummary {
  id: Id;
  number: string | null;
  date: IsoDate;
  narration: string;
  debitAccount: string | null;
  creditAccount: string | null;
  status: JournalStatus;
  amount: Money;
  isAdjusting: boolean;
  reversesNextYear: boolean;
}

export interface JournalLine {
  accountId: Id;
  accountCode: string;
  accountName: string;
  partyId: Id | null;
  partyName?: string;
  memo: string | null;
  debit: Money;
  credit: Money;
}

export interface Journal {
  id: Id;
  number: string | null;
  date: IsoDate;
  currency: string;
  narration: string;
  status: JournalStatus;
  isAdjusting: boolean;
  reversesNextYear: boolean;
  lines: JournalLine[];
  createdAt: string;
  postedAt: string | null;
}

export interface JournalDraft {
  id?: Id;
  date: IsoDate;
  currency: string;
  narration: string;
  isAdjusting: boolean;
  reversesNextYear: boolean;
  lines: Array<{ accountId: Id; partyId: Id | null; memo: string; debit: Money; credit: Money }>;
}

export interface NewBankAccount {
  /** An unused account in the chart. Null makes a new one. */
  ledgerAccountId: Id | null;
  /** The new account's code. Empty takes the next free one. */
  ledgerCode: string;
  name: string;
  bankName: string;
  accountNumber: string;
  branch: string;
  /** Empty is none. Negative is an overdraft. */
  openingBalance: string;
  openingDate: IsoDate;
}

// ───────────────────────── Documents ─────────────────────────

export type DocumentKind =
  | "invoice"
  | "creditNote"
  | "receipt"
  | "bill"
  | "debitNote"
  | "payment"
  | "journal"
  | "fundTransfer"
  | "purchaseOrder";

/** Approval/lifecycle status shared by documents (design: Draft · Submitted · Verified · Approved). */
export type DocumentStatus = "draft" | "submitted" | "verified" | "approved" | "void";

export type PaymentStatus = "unpaid" | "partiallyPaid" | "paid" | "overdue";

/** UNCL5305 subset used by BIS 3.0 (see design brief §3). */
export type VatCategory = "S" | "Z" | "E" | "AE" | "O";

export interface TaxCode {
  id: Id;
  code: string; // "VAT13"
  name: string; // "VAT 13%"
  category: VatCategory;
  ratePct: string; // "13"
  recoverable: boolean; // input VAT: false = non-recoverable (capitalised into expense)
  accountId?: Id;
  /** The ledger accounts its tax posts to. Empty when the person can't read the chart. */
  accountIds?: Id[];
  isDefault?: boolean;
}

export interface DocumentHistory {
  createdBy: string;
  createdAt: string;
  submittedAt?: string;
  verifiedBy?: string;
  verifiedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface DocumentTotals {
  lineNet: Money; // BT-106
  allowances: Money; // BT-107
  charges: Money; // BT-108
  taxExclusive: Money; // BT-109
  vat: Money; // BT-110
  taxInclusive: Money; // BT-112
  rounding: Money; // BT-114
  paid: Money; // BT-113
  due: Money; // BT-115
}

export interface InvoiceLine {
  n: number;
  itemName: string;
  description?: string;
  accountId: Id;
  accountName: string;
  quantity: string; // unrestricted decimals
  unitPrice: string; // unrestricted decimals
  vatCategory: VatCategory;
  vatRatePct: string;
  lineNet: Money; // 2dp
  charges?: Money;
  allowances?: Money;
  /** Copied from a billing item: its code, its exemption reason, and which item it was. */
  itemCode?: string;
  exemptionReason?: string;
  billingItemId?: Id;
}

/** An item or service a company bills. It only fills in a line, which keeps its own copy. */
export interface BillingItem {
  id: Id;
  name: string;
  description: string;
  hsCode: string;
  unitPrice: string;
  incomeAccountId: Id;
  category: VatCategory;
  /** The standard category's rate. Null on the others. */
  ratePct: string | null;
  exemptionReason: string;
  /** How many invoices and credit notes have a line from this item. */
  usedOnDocuments: number;
}

export type BillingItemForm = Omit<BillingItem, "id" | "usedOnDocuments">;

export interface InvoiceSummary {
  id: Id;
  number: string | null;
  customerId: Id;
  customerName: string;
  date: IsoDate;
  due: IsoDate;
  status: DocumentStatus;
  payment: PaymentStatus;
  total: Money;
  balance: Money;
  locked: boolean; // issued — immutable
  awaitingStep?: string; // "Waiting · Approver"
}

export interface Invoice extends InvoiceSummary {
  currency: string;
  /** The quotation this was made from. */
  fromQuotation?: { id: Id; number: string };
  buyerReference?: string;
  note?: string;
  /** The footer as it was when this was last saved as a draft. */
  footerText?: string;
  adjustments?: DocumentAdjustment[];
  /** What was taken when it was sold. Absent on a credit invoice. */
  cashSale?: CashSale;
  /** How to pay, one way per line, as it was when this was last saved as a draft. */
  paymentDetails?: string;
  /** The customer's tax number, as copied onto this document. */
  customerTaxId?: string;
  /** The customer's company register entry, as copied onto this document. */
  customerLegalRegistration?: string;
  /** The terms applied to this invoice, in days, as text. Absent is no terms. */
  paymentTermsDays?: string;
  lines: InvoiceLine[];
  totals: DocumentTotals;
  payments: Array<{ id: Id; number: string; date: IsoDate; method?: string; amount: Money }>;
  history: DocumentHistory;
}

export type CreditNoteReason =
  | "Billing error"
  | "Goods returned"
  | "Post-invoice discount"
  | "Price adjustment";

export interface CreditNoteSummary {
  id: Id;
  number: string | null;
  customerId: Id;
  customerName: string;
  date: IsoDate;
  appliedTo?: string | null; // preceding invoice number
  reason?: CreditNoteReason;
  status: DocumentStatus;
  total: Money;
}

export interface CreditNote extends CreditNoteSummary {
  precedingInvoiceId?: Id;
  /** What can still be used on another invoice (ADR-0034). */
  creditLeft?: Money;
  detail?: string;
  customerTaxId?: string;
  customerLegalRegistration?: string;
  lines: InvoiceLine[];
  totals: DocumentTotals;
  history: DocumentHistory;
}

export type ReceiptMethod = "Bank transfer" | "Cheque" | "Cash" | "eSewa" | "ConnectIPS";

export interface ReceiptApplication {
  invoiceId: Id;
  invoiceNumber: string;
  invoiceBalance: Money;
  applied: Money;
  tdsWithheld: Money;
}

export interface ReceiptSummary {
  id: Id;
  number: string | null;
  customerId: Id;
  customerName: string;
  date: IsoDate;
  method?: ReceiptMethod;
  appliedTo: string; // "INV-0233, INV-0231" or "Advance"
  status: DocumentStatus;
  amount: Money;
}

export interface Receipt extends ReceiptSummary {
  /** Known once it's posted, for matching it to a statement line. */
  journalId?: Id;
  bankAccountId: Id;
  bankAccountName: string;
  applications: ReceiptApplication[];
  advance: Money; // unapplied — held as customer advance
  note?: string;
  history: DocumentHistory;
  printedAt?: string;
}

export interface BillLine {
  n: number;
  description: string;
  accountId: Id;
  accountName: string;
  quantity: string;
  unitPrice: string;
  taxCodeId: Id;
  taxLabel: string;
  capital: boolean; // C · R toggle (capital vs revenue) for the VAT purchase book
  lineNet: Money;
}

export const DEBIT_NOTE_REASONS = [
  "Short delivery",
  "Goods returned",
  "Rate difference",
  "Billing error",
  "Other",
] as const;

export interface DebitNoteSummary {
  id: Id;
  number: string | null;
  supplierId: Id;
  supplierName: string;
  date: IsoDate;
  /** The bill it corrects. Null when it sits on the supplier's account. */
  againstBillId: Id | null;
  againstNumber: string | null;
  reason: string | null;
  status: DocumentStatus;
  total: Money;
  /** What is left to use against a later bill. */
  available: Money;
}

export interface DebitNote extends DebitNoteSummary {
  reasonDetail: string | null;
  lines: BillLine[];
  subtotal: Money;
  recoverableVat: Money;
  nonRecoverableTax: Money;
  history: DocumentHistory;
}

export interface NewDebitNote extends Omit<NewBill, "vendorInvoiceNo" | "due" | "attachmentIds"> {
  againstBillId: Id | null;
  reason: string;
  reasonDetail: string;
}

export interface GeneralLedgerLine {
  journalId: Id;
  date: IsoDate;
  documentType: string;
  number: string;
  reference: string | null;
  partyName: string | null;
  debit: Money;
  credit: Money;
  balance: Money;
}

export interface GeneralLedger {
  accountId: Id;
  code: string;
  name: string;
  from: IsoDate;
  to: IsoDate;
  opening: Money;
  closing: Money;
  totalDebit: Money;
  totalCredit: Money;
  allLines: number;
  matching: number;
  lines: GeneralLedgerLine[];
  parties: Array<{ id: Id; name: string }>;
}

/** A document a search found in a ledger, before anything is applied. */
export interface LedgerDocumentMatch {
  journalId: Id;
  number: string;
  date: IsoDate;
  narration: string | null;
  amount: Money;
}

export interface TrialBalanceAsOnRow {
  accountId: Id;
  code: string;
  name: string;
  group: AccountGroup;
  debit: Money;
  credit: Money;
}

export interface TrialBalanceAsOn {
  asOn: IsoDate;
  fiscalYearStart: IsoDate;
  rows: TrialBalanceAsOnRow[];
  totalDebit: Money;
  totalCredit: Money;
}

export interface CashFlowLine {
  accountId: Id;
  code: string;
  name: string;
  amount: Money;
}

export interface CashFlowReport {
  from: IsoDate;
  to: IsoDate;
  netResult: Money;
  operating: CashFlowLine[];
  investing: CashFlowLine[];
  financing: CashFlowLine[];
  openingCash: Money;
  closingCash: Money;
}

export interface AgeingReport {
  asAt: IsoDate;
  /** Upper bounds of each band but the last: [30, 60, 90] makes four bands. */
  bucketDays: number[];
  rows: Array<{ partyId: Id; partyName: string | null; buckets: Money[]; total: Money }>;
  bucketTotals: Money[];
  total: Money;
}

/** The receivable analysis screen's figures. */
export interface ReceivableAnalysis {
  asAt: IsoDate;
  total: Money;
  bucketDays: number[];
  bucketTotals: Money[];
  /** Null with no receipts in the last 12 months. */
  averageDaysToPay: number | null;
  overdueInvoices: number;
  /** Everyone with a balance, and everyone with a credit limit. */
  customers: ReceivableAnalysisCustomer[];
}

export interface ReceivableAnalysisCustomer {
  id: Id;
  name: string | null;
  /** Days to pay, as text. Empty is no terms. */
  paymentTermsDays: string;
  creditLimit: Money | null;
  outstanding: Money;
  /** How long the oldest amount has been past due. Null when nothing is. */
  daysPastDue: number | null;
}

/** The payable analysis screen's figures. */
export interface PayableAnalysis {
  asAt: IsoDate;
  total: Money;
  bucketDays: number[];
  bucketTotals: Money[];
  suppliersWithOpenBills: number;
  overdueBills: number;
  dueWithinWeek: Money;
  billsDueWithinWeek: number;
  /** What the suppliers' usual categories would withhold on their open bills: an estimate. */
  toDeduct: Money;
  withheldThisYear: Money;
  /** Only suppliers with a withholding rate and an open bill. */
  suppliers: PayableAnalysisSupplier[];
}

export interface PayableAnalysisSupplier {
  id: Id;
  name: string;
  category: string;
  ratePct: string;
  openBills: Money;
  toDeduct: Money;
}

/** Invoices or bills with something open, oldest due first. */
export interface OpenItems {
  items: OpenItem[];
  /** How many there are. `items` stops at a limit. */
  total: number;
}

export interface OpenItem {
  id: Id;
  number: string | null;
  partyId: Id;
  partyName: string;
  date: IsoDate;
  /** The due date, or the document's own date without one. */
  due: IsoDate;
  open: Money;
}

export interface VatReturn {
  from: IsoDate;
  to: IsoDate;
  output: Array<{
    category: string;
    ratePct: string | null;
    taxable: Money;
    tax: Money;
    documents: number;
  }>;
  input: Array<{
    recoverable: boolean;
    ratePct: string;
    taxable: Money;
    tax: Money;
    documents: number;
  }>;
  outputTax: Money;
  recoverableInputTax: Money;
  creditCarried: Money;
  payable: Money;
}

export interface WithholdingReport {
  withheld: Array<{
    documentId: Id;
    number: string;
    date: IsoDate;
    supplierName: string;
    supplierTaxId: string | null;
    code: string;
    ratePct: string;
    base: Money;
    amount: Money;
  }>;
  totalBase: Money;
  totalWithheld: Money;
  byCustomers: Array<{
    receiptId: Id;
    number: string;
    date: IsoDate;
    customerName: string;
    code: string | null;
    amount: Money;
  }>;
}

/** A report as it stands on screen, to write to a file. */
export interface ReportExport {
  title: string;
  period?: string;
  columns: Array<{ header: string; numeric?: boolean }>;
  rows: Array<Array<string | null>>;
  totals?: Array<string | null>;
}

/** All but draft, sent, confirmed and cancelled are worked out from what's recorded (ADR-0055). */
export type PurchaseOrderStatus =
  | "draft"
  | "sent"
  | "confirmed"
  | "partiallyReceived"
  | "received"
  | "billed"
  | "cancelled";

export interface PurchaseOrderSummary {
  id: Id;
  /** Null until someone starts to send it. */
  number: string | null;
  status: PurchaseOrderStatus;
  supplierId: Id;
  supplierName: string;
  date: IsoDate;
  expected: IsoDate;
  reference: string | null;
  total: Money;
  lineCount: number;
  linesReceived: number;
}

export interface PurchaseOrderOverview {
  onOrderAmount: Money;
  onOrderCount: number;
  toBillAmount: Money;
  toBillCount: number;
  overdueCount: number;
}

export interface PurchaseOrderPage extends Page<PurchaseOrderSummary> {
  overview: PurchaseOrderOverview;
}

export interface PurchaseOrderLine {
  id: Id;
  n: number;
  description: string;
  accountId: Id;
  accountName: string;
  quantity: string;
  unitPrice: string;
  vatRatePct: string;
  lineNet: Money;
  received: string;
  billed: string;
}

export interface PurchaseOrder extends PurchaseOrderSummary {
  currency: string;
  supplierTaxId?: string;
  deliverTo: string | null;
  terms: string | null;
  note: string | null;
  subtotal: Money;
  vat: Money;
  sentAt: string | null;
  lines: PurchaseOrderLine[];
  /** What arrived and when, which is what a goods receipt note lists. */
  receipts: Array<{ lineId: Id; description: string; quantity: string; date: IsoDate }>;
  /** Null number while the bill is a draft. */
  bills: Array<{ id: Id; number: string | null }>;
  history: DocumentHistory;
}

export interface NewPurchaseOrder {
  /** The draft being revised, absent when one is being created. */
  id?: Id;
  supplierId: Id;
  date: IsoDate;
  expected: IsoDate;
  reference?: string;
  deliverTo?: string;
  terms?: string;
  note?: string;
  lines: Array<{
    description: string;
    accountId: Id;
    quantity: string;
    unitPrice: string;
    vatRatePct: string;
    capital: boolean;
  }>;
}

/** Expired and converted aren't stored: the API works them out on reading (ADR-0054). */
export type QuotationStatus = "draft" | "sent" | "accepted" | "declined" | "expired" | "converted";

export interface QuotationSummary {
  id: Id;
  /** Null until it's sent. */
  number: string | null;
  status: QuotationStatus;
  customerId: Id;
  customerName: string;
  date: IsoDate;
  validUntil: IsoDate;
  reference: string | null;
  total: Money;
  invoiceId: Id | null;
  invoiceNumber: string | null;
}

export interface QuotationOverview {
  awaitingAmount: Money;
  awaitingCount: number;
  acceptedAmount: Money;
  acceptedCount: number;
  expiredCount: number;
}

export interface QuotationPage extends Page<QuotationSummary> {
  overview: QuotationOverview;
}

export interface Quotation extends QuotationSummary {
  currency: string;
  customerTaxId?: string;
  terms: string | null;
  note: string | null;
  taxExclusive: Money;
  allowances: Money;
  charges: Money;
  vat: Money;
  sentAt: string | null;
  lines: InvoiceLine[];
  adjustments: DocumentAdjustment[];
  history: DocumentHistory;
}

export interface NewQuotation {
  /** The draft being revised, absent when one is being created. */
  id?: Id;
  customerId: Id;
  date: IsoDate;
  validUntil: IsoDate;
  reference?: string;
  terms?: string;
  note?: string;
  adjustments?: DocumentAdjustment[];
  lines: NewInvoice["lines"];
}

export interface SupplierPaymentSummary {
  id: Id;
  /** Null while it's a draft waiting for approval. */
  number: string | null;
  status: "draft" | "posted";
  supplierId: Id;
  supplierName: string;
  date: IsoDate;
  method: string;
  reference: string | null;
  /** The numbers of the bills it settles. Empty when all of it is an advance. */
  appliedTo: string[];
  gross: Money;
  withheld: Money;
  /** What left the bank: the gross, less what was withheld. */
  net: Money;
  /** What no bill has taken yet, held against the supplier. */
  advance: Money;
}

export interface SupplierPayment extends SupplierPaymentSummary {
  /** For matching it to a statement line. */
  journalId?: Id;
  currency: string;
  paidFromName: string;
  withheldCode: string | null;
  withheldRate: string | null;
  applications: Array<{ billId: Id; billNumber: string; amount: Money }>;
  history: DocumentHistory;
}

export interface NewSupplierPayment {
  supplierId: Id;
  date: IsoDate;
  bankAccountId: Id;
  method: string;
  reference?: string;
  gross: Money;
  /** The category. The rate is the one in force, and the amount is worked out by the server (ADR-0031). */
  withholding?: { code: string };
  allocations: Array<{ billId: Id; amount: Money }>;
}

/** A category of tax withheld at source, with the rate in force today. */
export interface WithholdingCode {
  code: string;
  name: string;
  ratePct: string | null;
}

export interface BillSummary {
  id: Id;
  number: string | null; // our BILL-#### sequence
  supplierId: Id;
  supplierName: string;
  date: IsoDate;
  due: IsoDate;
  vendorInvoiceNo: string;
  status: DocumentStatus;
  total: Money;
  balance: Money;
  /** The amount before VAT: what TDS on this bill is charged on. */
  taxable?: Money;
}

export type SupplierCreditKind = "advance" | "debitNote";

/** What a supplier owes back and can be used against a bill. */
export interface SupplierCredit {
  kind: SupplierCreditKind;
  sourceId: Id;
  number: string;
  date: IsoDate;
  available: Money;
}

export interface UsedCredit {
  kind: SupplierCreditKind;
  sourceId: Id;
  number: string;
  date: IsoDate;
  amount: Money;
}

export interface BillCredits {
  available: SupplierCredit[];
  used: UsedCredit[];
}

export type CustomerCreditKind = "advance" | "creditNote";

/** What a customer holds in credit and can be used against an invoice (ADR-0034). */
export interface CustomerCredit {
  kind: CustomerCreditKind;
  sourceId: Id;
  number: string;
  date: IsoDate;
  available: Money;
}

export interface UsedCustomerCredit {
  kind: CustomerCreditKind;
  sourceId: Id;
  number: string;
  date: IsoDate;
  amount: Money;
}

export interface InvoiceCredits {
  available: CustomerCredit[];
  used: UsedCustomerCredit[];
}

export interface Bill extends BillSummary {
  /** The purchase order this was made from. */
  fromPurchaseOrder?: { id: Id; number: string };
  /** The terms applied, in days, as text. Absent is no terms, and "0" is due on receipt. */
  paymentTermsDays?: string;
  lines: BillLine[];
  subtotal: Money;
  recoverableVat: Money;
  nonRecoverableTax: Money;
  attachments: Array<{ id: Id; name: string; sizeBytes: number; contentType: string }>;
  history: DocumentHistory;
  approvalNote?: string;
}

export interface FundTransfer {
  id: Id;
  number: string | null; // "FT-0042"
  date: IsoDate;
  fromBankAccountId: Id;
  fromName: string;
  toBankAccountId: Id;
  toName: string;
  reference: string;
  amount: Money;
  status: DocumentStatus;
  history?: DocumentHistory;
}

export interface JournalLineView {
  accountCode: string;
  accountName: string;
  debit: Money;
  credit: Money;
}

// ───────────────────────── Approvals ─────────────────────────

export type ApprovalMode = "off" | "always" | "above";

export interface ApprovalRule {
  documentType: string;
  mode: ApprovalMode;
  threshold: Money | null;
  levels: 1 | 2;
  level1Approvers: Id[];
  level2Approvers: Id[];
}

export type ApprovalDocumentKind =
  | "employeePayment"
  | "invoice"
  | "creditNote"
  | "bill"
  | "debitNote"
  | "fundTransfer"
  | "payment"
  | "purchaseOrder"
  | "journal"
  | "yearClose";

export interface DocumentApprovalRequest {
  id: Id;
  status: "submitted" | "verified" | "approved" | "returned";
  levels: number;
  /** What it's waiting for, if anything. */
  step: "Verify" | "Approve" | null;
  /** Whether the person asking can take that step. */
  mayAct: boolean;
  /** Whether the person asking prepared it. */
  mine?: boolean;
  submittedBy: string;
  submittedAt: string;
  verifiedBy: string | null;
  verifiedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  returnedBy: string | null;
  returnedAt: string | null;
  returnReason: string | null;
}

export interface DocumentApproval {
  /** Whether it can't be issued without approval. */
  required: boolean;
  /** The latest request, which may have been returned. */
  request: DocumentApprovalRequest | null;
}

export interface ApprovalItem {
  id: Id;
  documentId: Id;
  documentNumber: string;
  partyName?: string;
  kind: DocumentKind | "yearClose" | "employeePayment";
  submittedBy: string;
  submittedAt: string;
  step: "Verify" | "Approve";
  amount: Money;
  actionLabel: "Verify" | "Approve";
  /** False where nothing on the API can send a document back; absent means it can. */
  canReturn?: boolean;
}

// ───────────────────────── Overview & reports ─────────────────────────

export interface OverviewData {
  asOf: IsoDate;
  kpis: Array<{
    key: "receivable" | "payable" | "cash" | "netProfit";
    label: string;
    value: Money;
    tone: "default" | "ok" | "bad";
  }>;
  queue: Array<{
    key: string;
    title: string;
    description: string;
    count: number;
    icon: string;
    tone: "default" | "warn" | "bad";
    to: string;
  }>;
  incomeExpense: Array<{ month: string; income: Money; expense: Money }>;
  netProfitFyToDate: Money;
  recent: InvoiceSummary[];
  cash: Array<{ bankAccountId: Id; name: string; bank: string; balance: Money }>;
  ageing: Array<{ bucket: string; amount: Money; pct: number; tone: "default" | "warn" | "bad" }>;
  bookmarks: Array<{ label: string; period: string; icon: string; to: string }>;
}

export interface ReportRow {
  label: string;
  depth: number;
  kind: "header" | "line" | "subtotal" | "total";
  current: Money | null;
  compare: Money | null;
  deltaPct: number | null;
}

export interface ProfitAndLoss {
  title: string;
  basis: "accrual" | "cash";
  period: string;
  compare: string | null;
  untaggedCount: number;
  rows: ReportRow[];
}

// ───────────────────────── Settings ─────────────────────────

/** Which parts of a new organisation's books are ready. */
export interface SetupStatus {
  company: boolean;
  localisation: boolean;
  chart: boolean;
  fiscalYear: boolean;
}

export interface CompanyProfile {
  legalName: string;
  tradeName: string;
  pan: string;
  vatRegistered: boolean;
  contactPhone?: string;
  contactEmail?: string;
  country?: string;
  currency?: string;
  address: {
    street: string;
    municipality: string;
    ward: string;
    district: string;
    province: string;
    postalCode: string;
  };
}

/**
 * Settings › Numbering (accounting ADR-0064): a number is the prefix, a dash, the fiscal-year code when asked for, and
 * four digits, restarting at 0001 each fiscal year.
 */
export interface NumberingSettings {
  /** Numbers carry the fiscal-year code after the prefix: INV-208384-0248. */
  includeFiscalYearCode: boolean;
  /** The current fiscal year's code, 208384; null when no year is open. */
  fiscalYearCode: string | null;
  /** The current fiscal year is the company's first in Rigo Accounting. */
  firstYear: boolean;
  sequences: NumberingSequence[];
}

/** Why a next number can't be set: the type has numbered documents, or it isn't the first fiscal year. */
export type NextNumberLock = "documents" | "year";

export interface NumberingSequence {
  code: string;
  label: string;
  /** Letters and digits; the dash is added. */
  prefix: string;
  defaultPrefix: string;
  next: number;
  lock: NextNumberLock | null;
}

export interface SaveNumbering {
  includeFiscalYearCode: boolean;
  sequences: Array<{ code: string; prefix: string; nextNumber: number | null }>;
}

export interface RoleDefinition {
  name: RoleName;
  builtIn: boolean;
  permissions: Permission[];
  members: number;
}

export interface ApprovalPolicy {
  documentType: string;
  separateApprover: boolean;
}

export interface UserRow {
  id: Id;
  fullName: string;
  email: string;
  role: RoleName;
  status: "active" | "invited" | "disabled";
  lastActiveAt?: string;
  accessExpires?: IsoDate | null;
}

export interface AuditEntry {
  id: Id;
  at: string;
  user: string;
  kind: "created" | "updated" | "approved" | "posted" | "locked" | "settings" | "login";
  document?: string;
  detail: string;
}

export type ComplianceMode = "posting" | "legallyReady" | "legal";

export interface LocalizationPack {
  country: "NP";
  name: "Nepal";
  currency: "NPR";
  fiscalCalendar: string; // "Shrawan–Ashadh"
  dateDisplay: "AD" | "BS" | "both";
  numberFormat: "lakh-crore";
  taxCodes: TaxCode[];
  tdsCategories: Array<{ code: string; name: string; ratePct: string }>;
  paymentMethods: ReceiptMethod[];
}

// ───────────────────────── List queries ─────────────────────────

export interface ListQuery {
  q?: string;
  customerId?: Id;
  fiscalYearId?: Id;
  month?: string; // "2026-08"
  from?: IsoDate;
  to?: IsoDate;
  minAmount?: Money;
  status?: DocumentStatus | QuotationStatus | PurchaseOrderStatus | "all";
  payment?: PaymentStatus | "all";
  cursor?: string;
  pageSize?: 25 | 50 | 100;
}

export interface Page<T> {
  items: T[];
  next: string | null;
  total: number;
}

// ───────────────────────── Commands (write DTOs) ─────────────────────────

/**
 * TODO(pass-3): the .NET API takes only the party's **id** here and reads the rest from
 * `parties.party` itself — the name, tax registration and postal address are copied onto the
 * document at that moment and frozen, because the legacy resolved them at report time and a re-run
 * VAT annexure then showed counterparties that were never filed (05-nepal-compliance.md §6.3 gap 8).
 *
 * Two consequences for whoever wires `http/` to `/api/v1`. The party has to exist first — a document
 * for an id the API cannot find is refused, not left blank. And the party's **country** is required,
 * because it is mandatory in the structured payload (BT-55); `Party` below has a free-text `address`
 * and a single `pan`, which the BIS brief §6 says explicitly is not sufficient, so this type needs
 * to grow structured identifiers and a structured address to match.
 */
/** An invoice entered into the books (ADR-0027). The customer's details come from the party record. */
export type PaymentOptionKind = "bank" | "wallet" | "cash" | "cheque";

/** A way customers can pay, on one of the company's bank or cash accounts. */
export interface PaymentOption {
  id: Id;
  kind: PaymentOptionKind;
  bankAccountId: Id;
  bankAccountName: string;
  accountNumber?: string;
  ledgerAccountId: Id;
  /** A wallet's name. Only a wallet has one. */
  name?: string;
  /** Who a cheque is payable to. Only a cheque has one. */
  payee?: string;
  shownOnInvoice: boolean;
}

export type PaymentOptionForm = Pick<
  PaymentOption,
  "kind" | "bankAccountId" | "name" | "payee" | "shownOnInvoice"
> & { id?: Id };

/** Money taken when an invoice was sold. Posting the invoice posts a receipt for it (ADR-0052). */
export interface CashSale {
  /** What was handed over. Anything above the invoice total is change. */
  amountReceived: Money;
  method: ReceiptMethod;
  bankAccountId: Id;
  bankAccountName?: string;
}

export interface NewInvoice {
  /** The draft being revised, absent when one is being created. See `NewCreditNote.id`. */
  id?: Id;
  customerId: Id;
  date: IsoDate;
  due?: IsoDate;
  /** The terms applied to this invoice, in days, as text. Absent is no terms. */
  paymentTermsDays?: string;
  /** BT-10. The API refuses to issue a document without this or an order reference (BR-CO-25). */
  buyerReference?: string;
  note?: string;
  adjustments?: DocumentAdjustment[];
  cashSale?: CashSale;
  lines: Array<{
    description: string;
    quantity: string;
    unitPrice: string;
    vatCategory: VatCategory;
    ratePct: string;
    accountId: Id;
    itemCode?: string;
    exemptionReason?: string;
    billingItemId?: Id;
    charges?: string;
    allowances?: string;
  }>;
}

export interface NewCreditNote {
  /**
   * The draft being revised, absent when one is being created.
   *
   * Without it every Save is a create, so pressing Save twice leaves two drafts and the second
   * edit of a document silently forks it. The API's own flow is draft → revise → issue and its
   * revise endpoint takes the id; this is the SPA saying which of the two it means.
   */
  id?: Id;
  customerId: Id;
  precedingInvoiceId?: Id;
  reason: CreditNoteReason;
  detail?: string;
  lines: Array<{
    itemName: string;
    quantity: string;
    unitPrice: string;
    vatCategory: VatCategory;
    ratePct: string;
    accountId: Id;
    itemCode?: string;
    exemptionReason?: string;
    billingItemId?: Id;
  }>;
}

export interface NewReceipt {
  customerId: Id;
  date: IsoDate;
  bankAccountId: Id;
  method: ReceiptMethod;
  amount: Money;
  applications: Array<{ invoiceId: Id; applied: Money; tdsWithheld: Money }>;
  note?: string;
}

/** See the note on `NewCreditNote`: the supplier is read from the party record the same way. */
export interface NewBill {
  /** The draft being revised, absent when one is being created. See `NewCreditNote.id`. */
  id?: Id;
  supplierId: Id;
  date: IsoDate;
  due: IsoDate;
  /** Days, as text. Absent is no terms, and "0" is due on receipt. */
  paymentTermsDays?: string;
  vendorInvoiceNo: string;
  lines: Array<{
    description: string;
    accountId: Id;
    quantity: string;
    unitPrice: string;
    taxCodeId: Id;
    capital: boolean;
  }>;
  attachmentIds: Id[];
}

export interface NewFundTransfer {
  date: IsoDate;
  fromBankAccountId: Id;
  toBankAccountId: Id;
  amount: Money;
  reference: string;
}

export interface ApiError {
  type: string; // RFC 9457 problem details
  title: string;
  status: number;
  detail?: string;
  errors?: Record<string, string[]>;
}

// ───────────────────────── Subscription (RigoSub, ADR-0009) ─────────────────────────

/**
 * Which banner the shell renders. RigoSub decides the code and the product renders the copy, so
 * the two can diverge without breaking this SPA (`SubMessageCodes` in
 * `Rigo.Platform.Contracts`). The SPA never switches on `status` — that vocabulary is RigoSub's
 * and is shown, not interpreted.
 */
export type SubMessageCode =
  | "none"
  | "renewal_upcoming"
  | "renewal_due"
  | "expiring"
  | "suspended"
  | "terminated"
  | "trial"
  | "trial_ended";

/**
 * What `GET /api/v1/subscription-status` answers.
 *
 * The browser never calls RigoSub: the API answers from its own short-TTL cache, so a login storm
 * costs RigoSub one call rather than one per open tab.
 *
 * `dueAmount` is a JSON number here rather than the decimal string the rest of this file insists
 * on. That is the API's shape (`decimal` through System.Text.Json), and it is a figure the SPA
 * only ever prints — nothing adds it to anything.
 */
export interface ReportTag {
  code: string;
  label: string;
  group: AccountGroup;
  category: string | null;
  /** For a company's own tag, the supplied tag it refines; absent for a supplied one (ADR-0065). */
  parentCode?: string | null;
  /** A company tag's id. */
  id?: Id | null;
  /** A company tag no longer offered; kept for the finals that name it. */
  archived?: boolean;
}

/** A company tag to add under a supplied one (ADR-0065). */
export interface NewCompanyTag {
  parentCode: string;
  code: string;
  label: string;
}

export interface ReportFramework {
  code: string;
  name: string;
  tags: ReportTag[];
}

/** One account in a framework. The tag is the confirmed one, or else the suggested one. */
export interface AccountMappingRow {
  accountId: Id;
  code: string;
  name: string;
  group: AccountGroup;
  isActive: boolean;
  balance: Money;
  status: "unmapped" | "suggested" | "confirmed" | "excluded";
  tagCode: string | null;
  suggestedBecause: string | null;
  exclusionReason: string | null;
}

export interface MappingDecision {
  accountId: Id;
  /** Null to leave the account out of the framework, which needs a reason. */
  tagCode: string | null;
  exclusionReason: string | null;
}

export interface MappingChange {
  oldTagCode: string | null;
  newTagCode: string | null;
  exclusionReason: string | null;
  changedAt: string;
}

export interface ReportTemplateSummary {
  code: string;
  frameworkCode: string;
  statement: "pl" | "bs";
  name: string;
  version: string;
  /** Read-only: a company clones it to change it. */
  supplied: boolean;
}

export interface TemplateLine {
  code: string;
  kind: "head" | "tag" | "result" | "sum" | "grand";
  label: string;
  tags: string[];
  /** What a sum adds and takes away, such as +L10 and -L20. */
  formula: string[];
  negate: boolean;
  strong: boolean;
  /** On the total lines that prove the statement against the trial balance, which have to stay. */
  tie: string | null;
}

export interface ReportTemplateDetail {
  summary: ReportTemplateSummary;
  lines: TemplateLine[];
}

export interface TemplatePreview {
  frameworkCode: string;
  statement: "pl" | "bs";
  lines: TemplateLine[];
  from: IsoDate | null;
  to: IsoDate;
}

export interface StatementQuery {
  templateCode: string;
  /** The start of a profit and loss period. A balance sheet runs from its fiscal year's start. */
  from: IsoDate | null;
  to: IsoDate;
  compareFrom: IsoDate | null;
  compareTo: IsoDate | null;
}

export interface ReportLine {
  code: string;
  kind: "head" | "tag" | "result" | "sum" | "grand";
  label: string;
  strong: boolean;
  amount: Money | null;
  compare: Money | null;
  suggestedAccounts: number;
  accounts: Array<{
    accountId: Id;
    code: string;
    name: string;
    tagCode: string | null;
    status: AccountMappingRow["status"];
    amount: Money;
  }>;
}

export interface StatementOutsider {
  accountId: Id;
  code: string;
  name: string;
  reason: string;
  amount: Money;
}

export interface RenderedStatement {
  templateCode: string;
  templateName: string;
  templateVersion: string;
  /** Null on a balance sheet, which is at one date. */
  from: IsoDate | null;
  to: IsoDate;
  compareFrom: IsoDate | null;
  compareTo: IsoDate | null;
  lines: ReportLine[];
  unmapped: StatementOutsider[];
  excluded: StatementOutsider[];
  checks: {
    unconfirmed: number;
    suggested: number;
    unassignedTotal: Money;
    excludedTotal: Money;
    difference: Money;
  };
  final: { id: Id; finalizedAt: string; differs: boolean } | null;
}

export interface SetupStepState {
  step: string;
  status: "done" | "later";
}

export interface SubscriptionStatus {
  /** What RigoSub grants, by code. Null or absent is no instruction, so nothing's held back. */
  features?: string[] | null;
  /** Whether this deployment talks to RigoSub at all. False means "render nothing". */
  enabled: boolean;
  /** ACTIVE · DUE · OVERDUE · GRACE · EXTENDED · SUSPENDED · CANCELLED · TRIAL · TRIAL_ENDED, or null when RigoSub knows nothing. */
  status: string | null;
  messageCode: SubMessageCode;
  /** Whether the books are locked (402): suspended, cancelled, or a trial that has ended. */
  blocked: boolean;
  /** STARTER, STANDARD or PRO (suite ADR-0096). */
  planCode?: string | null;
  /** The last day of a free trial. */
  trialEndsOn?: IsoDate | null;
  /** The add-ons held, by code: REPORTING. */
  addOns?: string[] | null;
  /** MONTHLY, QUARTERLY or YEARLY. */
  cycle?: string | null;
  /** Users the plan includes, and those bought beyond them. The seat limit is the two together. */
  includedUsers?: number | null;
  extraUsers?: number | null;
  /** When the next bill issues. */
  renewsOn?: IsoDate | null;
  /** When locked books are deleted. Null unless they're locked. */
  keptUntil?: IsoDate | null;
  /** Outstanding, pre-tax NPR. */
  dueAmount: number;
  dueDate: IsoDate | null;
  keepAliveUntil: IsoDate | null;
  /**
   * The last day already billed (suite ADR-0082): what the `renewal_upcoming` notice is dated by.
   * Null when RigoSub has no next bill for the organisation.
   */
  paidThrough: IsoDate | null;
}

// ───────────────────────── Checkout (suite ADR-0097) ─────────────────────────
//
// RigoSub's checkout, passed through the API. Every figure here is RigoSub's: the screen shows them and does no
// arithmetic on money of its own.

/** A price by the month and by the year. */
export interface CheckoutPrice {
  monthly: number;
  yearly: number;
}

/** A plan on sale. */
export interface CheckoutPlan {
  code: string;
  name: string;
  description: string | null;
  price: CheckoutPrice;
  includedUsers: number;
  /** What a user beyond those costs; null when the plan sells none. */
  extraUser: CheckoutPrice | null;
  /** Feature codes, in catalogue order. */
  features: string[];
}

/** An add-on switched on or off, priced by plan. */
export interface CheckoutAddOn {
  code: string;
  name: string;
  description: string | null;
  /** By plan code. A plan missing here doesn't sell it. */
  prices: Record<string, CheckoutPrice>;
  features: string[];
}

/** Who the tax invoice is made out to. */
export interface CheckoutBilling {
  name: string;
  taxId: string | null;
  address: string | null;
  email: string | null;
}

/**
 * How an order is paid (suite ADR-0102). eSewa, Khalti and connectIPS are Nepal's; each online one is the name of the
 * gateway that takes it. CARD is an international card, which no gateway takes yet.
 */
export type PaymentMethod = "ESEWA" | "KHALTI" | "CONNECT_IPS" | "CARD" | "BANK_TRANSFER";

/** Everything the checkout shows before anything is chosen. */
export interface CheckoutOffer {
  currency: string;
  taxPercent: number;
  plans: CheckoutPlan[];
  addOns: CheckoutAddOn[];
  current: {
    status: string;
    planCode: string;
    cycle: string;
    addOns: string[];
    extraUsers: number;
    trialEndsOn: IsoDate | null;
  };
  billing: CheckoutBilling;
  /** The country the prices are for (suite ADR-0101); null is the home price book. */
  country?: string | null;
  /** Paid for, so the country can't change. */
  countryLocked?: boolean;
  /** The countries sold to, each with its currency. */
  countries?: Array<{ code: string; currency: string }> | null;
  /** How it can be paid: CARD, ESEWA, CONNECT_IPS, BANK_TRANSFER. */
  paymentMethods?: string[] | null;
  /** An order placed for a bank transfer that hasn't arrived yet (design D4). */
  awaitingTransfer?: {
    checkoutId: Id;
    reference: string;
    total: number;
    currency: string;
    orderedOn: IsoDate;
    transfer: BankTransferDetails;
  } | null;
}

export type CheckoutCycle = "MONTHLY" | "YEARLY";

/** What the customer chose. */
export interface CheckoutChoice {
  planCode: string;
  cycle: CheckoutCycle;
  addOns: string[];
  extraUsers: number;
  /** The country billed in, which picks the price book; null keeps the subscription's (suite ADR-0101). */
  country?: string | null;
}

/** What a choice costs today: the first bill, drafted as RigoSub will issue it. */
export interface CheckoutQuote {
  lines: Array<{ label: string; detail: string; amount: number }>;
  subtotal: number;
  taxPercent: number;
  tax: number;
  total: number;
  currency: string;
  periodStart: IsoDate;
  periodEnd: IsoDate;
  renewsOn: IsoDate;
  /** A whole cycle from the renewal on, before tax. */
  perCycle: number;
  /** Feature codes held today that this choice wouldn't have. */
  losing: string[];
  /**
   * SUBSCRIBE: a trial buys a plan and this is its first bill. CHANGE: a paid subscription changes at once and this is
   * the difference for the rest of the period already paid (suite ADR-0099).
   */
  kind: "SUBSCRIBE" | "CHANGE" | "PAY_BILL";
  /** How it can be paid: CARD, ESEWA, CONNECT_IPS, BANK_TRANSFER (suite ADR-0101). */
  paymentMethods?: string[] | null;
}

/** An order placed, and where to pay it. */
/** Rigo's account a bank transfer goes to, as the pro-forma prints it (design D4). */
export interface BankTransferDetails {
  accountName: string;
  bankName: string;
  accountNumber: string;
  branch: string | null;
  swiftCode: string | null;
}

export interface CheckoutOrder {
  checkoutId: Id;
  quote: CheckoutQuote;
  payment: {
    gateway: string;
    reference: string;
    redirectUrl: string | null;
    /** A gateway's development stand-in: no money moves, and the payment page offers approve and decline. */
    simulated: boolean;
    /** Where to send a bank transfer, for an order awaiting one; nothing else changes until it arrives. */
    transfer?: BankTransferDetails | null;
    /**
     * Signed fields the browser posts to `redirectUrl` (eSewa, connectIPS); null when it simply goes there (Khalti).
     * Passed on unchanged.
     */
    form?: Record<string, string> | null;
  };
}

/** What became of a payment. */
export interface CheckoutResult {
  checkoutId: Id;
  outcome: "PAID" | "DECLINED";
  billId: string | null;
  invoiceRef: string | null;
  message: string | null;
}

// ───────────────────────── Managing a subscription (suite ADR-0098) ─────────────────────────

export type ChangeKind = "DOWNGRADE" | "REMOVE_ADD_ON" | "CYCLE" | "CANCEL";

/** A change scheduled for the next renewal, with Undo until then. */
export interface PendingChange {
  id: Id;
  kind: ChangeKind;
  effectiveOn: IsoDate;
  planCode: string | null;
  addOnCode: string | null;
  cycle: CheckoutCycle | null;
  /** Feature codes that lock on that day because of it. */
  losing: string[];
}

/** One of the subscription's bills. */
export interface SubscriptionBill {
  id: string;
  issueDate: IsoDate;
  period: string;
  /** Before tax. */
  total: number;
  balance: number;
  status: string;
  invoiceRef: string | null;
}

/** A paid subscription as an administrator manages it. Every figure is RigoSub's. */
export interface SubscriptionAccount {
  status: string;
  current: CheckoutChoice;
  includedUsers: number;
  currency: string;
  taxPercent: number;
  /** A whole cycle on today's terms, before tax. */
  perCycle: number;
  /** The next bill, and the day a scheduled change takes effect. */
  renewsOn: IsoDate | null;
  /** The day a scheduled cancellation takes effect. */
  cancelsOn: IsoDate | null;
  trialEndsOn: IsoDate | null;
  pending: PendingChange[];
  /** The terms from the renewal once the scheduled changes apply, and a cycle's cost then. */
  afterRenewal: { terms: CheckoutChoice; perCycle: number } | null;
  billing: CheckoutBilling;
  bills: SubscriptionBill[];
  /** The country it's billed in, fixed once subscribed (suite ADR-0101). */
  country?: string | null;
}

// ───────────────────────── Help and support (ADR-0010) ─────────────────────────

/**
 * What `GET /api/v1/support/help-status` answers: whether the shell offers Help at all.
 *
 * The guides and the tickets live in the platform support module; this API only forwards. The
 * browser never calls the platform, and never names an organisation — the session does.
 */
export interface HelpStatus {
  /** `PlatformSupport:HelpEnabled` is on and a support host is configured. False: no Help anywhere. */
  enabled: boolean;
  /** Help is on and this role holds `support.tickets.manage`. */
  canRaiseTickets: boolean;
}

/** A guide as a card: the curated list, a topic page, previous and next. */
export interface HelpArticleCard {
  id: Id;
  slug: string;
  title: string;
  summary?: string | null;
  sectionName: string;
  categorySlug: string;
  categoryName: string;
}

/** A topic on the Help home page. */
export interface HelpCategoryCard {
  id: Id;
  slug: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  articleCount: number;
}

/** The Help home page, for this reader's rung. */
export interface HelpHome {
  workspaceName: string;
  categories: HelpCategoryCard[];
  recommended: HelpArticleCard[];
  latestUpdates: Array<{ slug: string; title: string; publishedOn: string }>;
}

/** One topic and the guides in it this reader may open. */
export interface HelpCategoryPage {
  id: Id;
  slug: string;
  name: string;
  description?: string | null;
  articles: HelpArticleCard[];
}

/** One guide. `contentHtml` is sanitized by the platform, and again on render by PlatformHtml. */
export interface HelpArticle {
  id: Id;
  slug: string;
  title: string;
  summary?: string | null;
  contentHtml: string;
  categorySlug: string;
  categoryName: string;
  sectionName: string;
  publishedAt?: string | null;
  updatedAt?: string | null;
  showLastUpdated: boolean;
  toc: Array<{ id: string; text: string; level: number }>;
  previous?: HelpArticleCard | null;
  next?: HelpArticleCard | null;
}

/**
 * What asking for a guide produced. `restricted` is the API's 403 `help_article_restricted`: the
 * guide exists and this reader may not open it, and `detail` is the sentence that says who may.
 */
export type HelpArticleOutcome =
  | { kind: "article"; article: HelpArticle }
  | { kind: "restricted"; title: string; requiredRole: string; detail: string }
  | { kind: "notFound" };

/** One search hit. `excerpt` is `ts_headline` output: `<mark>` around matches in unescaped text. Render with Excerpt, never as HTML. */
export interface HelpSearchHit {
  slug: string;
  title: string;
  categoryName: string;
  excerpt: string;
  rank: number;
}

/** OPEN · IN_PROGRESS · AWAITING_INFO · RESOLVED · CLOSED — the platform's words, shown not decided. */
export type SupportTicketStatus = string;

/** A ticket as a list row. */
export interface SupportTicketSummary {
  id: Id;
  ticketNo: string;
  title: string;
  category: string;
  status: SupportTicketStatus;
  /** HIGH · MEDIUM · LOW. */
  customerPriority: string;
  raisedByName?: string | null;
  assignedAgentName?: string | null;
  dueOn?: IsoDate | null;
  createdAt: string;
  lastActivityAt: string;
  closedAt?: string | null;
}

/** A file on a ticket or a message. */
export interface SupportAttachment {
  id: Id;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

/** One entry in a ticket's conversation. `bodyHtml` is rendered only through PlatformHtml, which sanitizes it. */
export interface SupportMessage {
  id: Id;
  kind: string;
  bodyHtml: string;
  authorName: string;
  isFromAgent: boolean;
  createdAt: string;
  attachments: SupportAttachment[];
}

/** A ticket with its conversation. */
export interface SupportTicketDetail {
  ticket: SupportTicketSummary;
  raisedByEmail?: string | null;
  firstResponseAt?: string | null;
  resolvedAt?: string | null;
  messages: SupportMessage[];
  attachments: SupportAttachment[];
}

/** A file staged before the ticket or reply it belongs to exists. */
export interface SupportStagedFile {
  stagingKey: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/** Raising a ticket. Who raised it, and for which organisation, the API takes from the session. */
export interface NewSupportTicket {
  title: string;
  category: string;
  customerPriority: "HIGH" | "MEDIUM" | "LOW";
  descriptionHtml: string;
  attachmentKeys: string[];
}
